import {useTranslation} from '@wix/yoshi-flow-editor'
import React, {useCallback} from 'react'
import {Counter} from 'wix-ui-tpa/cssVars'
import {SEATING_ERROR} from '../../../../../constants'
import {getPlaceInfoText} from '../../../../../selectors/seating/place'
import {checkDonationError} from '../../../../../services/donations'
import {DONATION_ERROR} from '../../../../../types'
import {TicketAreaCounterProps} from './interfaces'
import {classes} from './ticket-area-counter.st.css'

export const TicketAreaCounter = ({
  place,
  value,
  placeStock,
  setValue,
  setPlaceDonationError,
  setSeatingError,
  orderRemainder,
  showAccessibilityMode,
  chooseSeatMode,
  chooseMultipleSeatsMode,
  isPlaceSelected,
}: TicketAreaCounterProps) => {
  const {t} = useTranslation()
  const {quantity, ticket, donation} = place

  const min = showAccessibilityMode ? 0 : 1
  const max = chooseSeatMode ? 1 : placeStock + quantity

  const placeInfoText = getPlaceInfoText(place, t)

  const handleCounterChange = useCallback(
    (rawValue: string) => {
      let newValue = Number(rawValue)

      if (rawValue === '' || !Number.isInteger(newValue)) {
        return
      }

      if (newValue < min) {
        newValue = min
      } else if (newValue > max) {
        newValue = max
      }

      if (newValue > value) {
        const error = checkDonationError(
          ticket,
          donation,
          chooseSeatMode || chooseMultipleSeatsMode ? {excludeErrors: [DONATION_ERROR.MINIMUM_NOT_REACHED]} : undefined,
        )
        if (error) {
          setPlaceDonationError({placeId: place.id, error})
          return
        }
      }

      if (newValue > orderRemainder && !chooseSeatMode) {
        setSeatingError(SEATING_ERROR.TICKET_LIMIT_REACHED)
        return
      }

      setValue(newValue)
    },
    [
      max,
      min,
      value,
      orderRemainder,
      chooseSeatMode,
      chooseMultipleSeatsMode,
      setSeatingError,
      setPlaceDonationError,
      showAccessibilityMode,
      place.id,
      ticket,
      donation,
    ],
  )

  return (
    <Counter
      className={classes.counter}
      incrementAriaLabel={`${t('seatings_area_counter_increment')} ${placeInfoText}`}
      decrementAriaLabel={`${t('seatings_area_counter_decrement')} ${placeInfoText}`}
      inputAriaLabel={t('seatings_area_counter_input')}
      errorTooltipMaxWidth={300}
      value={value}
      min={min}
      max={max}
      onChange={handleCounterChange}
      aria-pressed={isPlaceSelected}
    />
  )
}
